import { useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import { apiFetch } from './auth';

const statsCache = new Map();
const inflight = new Map();
const updateListeners = new Set();

const EMPTY_STATS = {
  completed_percent: 0,
  unfinished_percent: 0,
  has_overdue: false,
};

export async function fetchEmployeeTaskStats(employeeIds) {
  const ids = [...new Set(
    (employeeIds || [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0)
  )];

  if (!ids.length) return new Map();

  const uncached = ids.filter((id) => !statsCache.has(id));

  if (uncached.length) {
    const key = uncached.slice().sort((a, b) => a - b).join(',');
    if (!inflight.has(key)) {
      inflight.set(
        key,
        (async () => {
          try {
            const res = await apiFetch(
              `${API_URL}/tasks/employee-stats?employee_ids=${encodeURIComponent(key)}`
            );
            if (res.ok) {
              const data = await res.json();
              for (const item of data.items || []) {
                statsCache.set(Number(item.employee_id), item);
              }
            }
          } finally {
            for (const id of uncached) {
              if (!statsCache.has(id)) {
                statsCache.set(id, { employee_id: id, ...EMPTY_STATS });
              }
            }
            inflight.delete(key);
          }
        })()
      );
    }
    await inflight.get(key);
  }

  const result = new Map();
  for (const id of ids) {
    result.set(id, statsCache.get(id) || { employee_id: id, ...EMPTY_STATS });
  }
  return result;
}

function normalizeEmployeeIds(employeeIds) {
  return [...new Set(
    (employeeIds || [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0)
  )];
}

function notifyStatsUpdate(employeeIds, map) {
  for (const listener of updateListeners) {
    listener(employeeIds, map);
  }
}

export function subscribeEmployeeTaskStats(listener) {
  updateListeners.add(listener);
  return () => updateListeners.delete(listener);
}

export function useEmployeeTaskStats(employeeIds) {
  const idsKey = useMemo(
    () => normalizeEmployeeIds(employeeIds).sort((a, b) => a - b).join(','),
    [employeeIds]
  );

  const [statsMap, setStatsMap] = useState(() => new Map());
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!idsKey) return undefined;

    const ids = new Set(idsKey.split(',').map(Number));

    return subscribeEmployeeTaskStats((updatedIds) => {
      if (updatedIds.some((id) => ids.has(id))) {
        setRefreshTick((tick) => tick + 1);
      }
    });
  }, [idsKey]);

  useEffect(() => {
    if (!idsKey) {
      setStatsMap(new Map());
      return undefined;
    }

    let cancelled = false;
    const ids = idsKey.split(',').map(Number);

    fetchEmployeeTaskStats(ids).then((map) => {
      if (!cancelled) setStatsMap(map);
    });

    return () => {
      cancelled = true;
    };
  }, [idsKey, refreshTick]);

  return statsMap;
}

export function invalidateEmployeeTaskStats(employeeIds) {
  const ids = new Set(normalizeEmployeeIds(employeeIds));
  for (const id of ids) {
    statsCache.delete(id);
  }
  for (const key of [...inflight.keys()]) {
    const keyIds = key.split(',').map(Number);
    if (keyIds.some((id) => ids.has(id))) {
      inflight.delete(key);
    }
  }
}

export async function refreshEmployeeTaskStats(employeeIds) {
  const ids = normalizeEmployeeIds(employeeIds);
  if (!ids.length) return new Map();

  invalidateEmployeeTaskStats(ids);
  const map = await fetchEmployeeTaskStats(ids);
  notifyStatsUpdate(ids, map);
  return map;
}

export function handleEmployeeTaskStatsSocketUpdate(payload) {
  const ids = normalizeEmployeeIds(payload?.employee_ids);
  if (!ids.length) return Promise.resolve(new Map());
  return refreshEmployeeTaskStats(ids);
}
